import { Injectable } from '@angular/core';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import { SERVICE_CONSTANTS } from './service.constants';

/**
 * App Update Notification Service
 * Handles cross-tab communication for app updates and notification broadcasting
 */
@Injectable({
  providedIn: 'root'
})
export class AppUpdateNotificationService {
  private updateChannel: BroadcastChannel;
  private updateNotificationShown = false;
  private lastUpdateTimestamp = 0;

  constructor(private _fuseConfirmationService: FuseConfirmationService) {}

  initialize(): void {
    this.updateChannel = new BroadcastChannel('app-updates');
    this.setupCrossTabListener();
    this.setupServiceWorkerEventListener();
  }

  private setupCrossTabListener(): void {
    this.updateChannel.addEventListener('message', (event) => {
      if (event.data.type === SERVICE_CONSTANTS.EVENTS.NEW_VERSION_AVAILABLE) {
        console.log('Received update notification from another tab');
        if (this.shouldShowNotification(event.data.timestamp)) {
          this.showUpdateNotification();
        }
      }
    });
  }

  private shouldShowNotification(timestamp: number): boolean {
    return !this.updateNotificationShown && timestamp !== this.lastUpdateTimestamp;
  }

  broadcastUpdateAvailable(): void {
    const timestamp = Date.now();

    if (this.isDuplicateBroadcast(timestamp)) {
      console.log('Recent broadcast detected, skipping duplicate');
      return;
    }

    this.lastUpdateTimestamp = timestamp;
    this.broadcastToOtherTabs(timestamp);
    this.showUpdateNotification();
  }

  private isDuplicateBroadcast(timestamp: number): boolean {
    return timestamp - this.lastUpdateTimestamp < SERVICE_CONSTANTS.UPDATE_NOTIFICATION.DUPLICATE_PREVENTION_MS;
  }

  private broadcastToOtherTabs(timestamp: number): void {
    this.updateChannel.postMessage({
      type: SERVICE_CONSTANTS.EVENTS.NEW_VERSION_AVAILABLE,
      timestamp: timestamp
    });
  }

  private showUpdateNotification(): void {
    if (this.updateNotificationShown) {
      console.log('Update notification already shown in this tab');
      return;
    }

    this.updateNotificationShown = true;
    this.scheduleNotificationReset();
    this.showUpdateAvailableDialog();
  }

  private scheduleNotificationReset(): void {
    setTimeout(() => {
      this.updateNotificationShown = false;
    }, SERVICE_CONSTANTS.UPDATE_NOTIFICATION.NOTIFICATION_RESET_MS);
  }

  private setupServiceWorkerEventListener(): void {
    // Listen for custom service worker update events dispatched from main.ts
    window.addEventListener(SERVICE_CONSTANTS.EVENTS.SW_UPDATED_CUSTOM, () => {
      this.broadcastUpdateAvailable();
    });
    console.log('Service Worker update event listener initialized in AppUpdateNotificationService');
  }

  private showUpdateAvailableDialog(): void {
    const notification = {
      title: 'Atualização Disponível',
      body: 'Uma nova versão do app está disponível. Atualize para obter as últimas atualizações e melhorias!',
      icon: 'heroicons_outline:arrow-path'
    };

    // Show confirmation dialog for app update
    const dialogRef = this._fuseConfirmationService.open({
      title: notification.title,
      message: notification.body,
      icon: {
        show: true,
        name: notification.icon,
        color: 'info'
      },
      actions: {
        confirm: {
          show: true,
          label: 'Atualizar Agora',
          color: 'primary'
        },
        cancel: {
          show: true,
          label: 'Mais Tarde'
        }
      },
      dismissible: false
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === 'confirmed') {
        window.location.reload();
      }
    });
  }

  cleanup(): void {
    if (this.updateChannel) {
      this.updateChannel.close();
    }
  }
}
